import { supabase } from '@/integrations/supabase/client';

export type SupportTicketStatus = 'open' | 'working' | 'completed';

export interface SupportAttachment {
  id: string;
  ticket_id: string;
  company_id: string;
  file_path: string;
  public_url: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

export interface SupportTicket {
  id: string;
  company_id: string;
  created_by: string;
  title: string;
  description: string;
  status: SupportTicketStatus;
  handled_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  creator?: {
    id: string;
    full_name: string | null;
    email: string | null;
  } | null;
  attachments?: SupportAttachment[];
}

export interface CreateSupportTicketInput {
  companyId: string;
  userId: string;
  title: string;
  description: string;
}

export interface SupportUploadResult {
  filePath: string;
  publicUrl: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
}

export class SupportService {
  private static readonly BUCKET_NAME = 'support-attachments';
  private static readonly VALID_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  private static readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  static async getTickets(): Promise<SupportTicket[]> {
    const { data, error } = await supabase
      .from('support_tickets' as any)
      .select(`
        *,
        creator:user_profiles!support_tickets_created_by_fkey(
          id,
          full_name,
          email
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const tickets = (data || []) as SupportTicket[];
    if (tickets.length === 0) {
      return [];
    }

    const ticketIds = tickets.map((ticket) => ticket.id);
    const attachments = await this.getAttachmentsForTickets(ticketIds);
    const attachmentsByTicket = new Map<string, SupportAttachment[]>();

    for (const attachment of attachments) {
      const existing = attachmentsByTicket.get(attachment.ticket_id) || [];
      existing.push(attachment);
      attachmentsByTicket.set(attachment.ticket_id, existing);
    }

    return tickets.map((ticket) => ({
      ...ticket,
      attachments: attachmentsByTicket.get(ticket.id) || [],
    }));
  }

  static async createTicket(input: CreateSupportTicketInput): Promise<SupportTicket> {
    const payload = {
      company_id: input.companyId,
      created_by: input.userId,
      title: input.title.trim(),
      description: input.description.trim(),
      status: 'open' as SupportTicketStatus,
    };

    const { data, error } = await supabase
      .from('support_tickets' as any)
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      throw error;
    }

    return data as SupportTicket;
  }

  static async updateTicketStatus(
    ticketId: string,
    status: SupportTicketStatus,
    handledBy: string
  ): Promise<void> {
    const updatePayload: Record<string, string | null> = {
      status,
      handled_by: handledBy,
      resolved_at: status === 'completed' ? new Date().toISOString() : null,
    };

    const { error } = await supabase
      .from('support_tickets' as any)
      .update(updatePayload)
      .eq('id', ticketId);

    if (error) {
      throw error;
    }
  }

  static async uploadAndAttachScreenshots(
    companyId: string,
    ticketId: string,
    files: FileList | File[],
    onProgress?: (uploaded: number, total: number) => void
  ): Promise<SupportAttachment[]> {
    const fileArray = Array.from(files);
    const uploadedRows: SupportAttachment[] = [];

    for (let i = 0; i < fileArray.length; i += 1) {
      const file = fileArray[i];
      const uploadResult = await this.uploadSingleScreenshot(companyId, ticketId, file);

      const { data, error } = await supabase
        .from('support_ticket_attachments' as any)
        .insert({
          ticket_id: ticketId,
          company_id: companyId,
          file_path: uploadResult.filePath,
          public_url: uploadResult.publicUrl,
          file_name: uploadResult.fileName,
          mime_type: uploadResult.mimeType,
          size_bytes: uploadResult.sizeBytes,
        })
        .select('*')
        .single();

      if (error) {
        throw error;
      }

      uploadedRows.push(data as SupportAttachment);

      if (onProgress) {
        onProgress(i + 1, fileArray.length);
      }
    }

    return uploadedRows;
  }

  private static async getAttachmentsForTickets(ticketIds: string[]): Promise<SupportAttachment[]> {
    const { data, error } = await supabase
      .from('support_ticket_attachments' as any)
      .select('*')
      .in('ticket_id', ticketIds)
      .order('created_at', { ascending: true });

    if (error) {
      throw error;
    }

    return (data || []) as SupportAttachment[];
  }

  private static async uploadSingleScreenshot(
    companyId: string,
    ticketId: string,
    file: File
  ): Promise<SupportUploadResult> {
    if (!this.VALID_IMAGE_TYPES.includes(file.type)) {
      throw new Error('Sadece JPG, PNG veya WebP dosyaları yüklenebilir.');
    }

    if (file.size > this.MAX_FILE_SIZE) {
      throw new Error('Dosya boyutu 5MB sınırını aşıyor.');
    }

    const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '');
    const safeBaseName = baseName.replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileName = `${Date.now()}_${Math.random().toString(36).slice(2)}_${safeBaseName}.${extension}`;
    const filePath = `${companyId}/support/${ticketId}/${fileName}`;

    const { error } = await supabase.storage
      .from(this.BUCKET_NAME)
      .upload(filePath, file, {
        upsert: false,
        cacheControl: '3600',
      });

    if (error) {
      throw error;
    }

    const {
      data: { publicUrl },
    } = supabase.storage.from(this.BUCKET_NAME).getPublicUrl(filePath);

    return {
      filePath,
      publicUrl,
      fileName: file.name,
      mimeType: file.type || null,
      sizeBytes: file.size,
    };
  }
}
